import * as React from 'react';
import { DatePickerRange, Form, CustomSubmitButton, CustomSubmitButtonType } from "soprasteria-ui-library";
import { CareProviderDropdown } from "../../CareProviderDropdown/CareProviderDropdown";
import { useAuth } from '../../../App';
import { ISearchEndedListing } from '../../../models/IEndedListingPatient';
import { ListingsApi } from '../../../api/ListingsApi';
import { ICareProvider } from '../../../models/ICareProvider';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";

interface ISearchEndedListingForm {
    searchEndedListings: (data: ISearchEndedListing) => void;
    isParaply: boolean;
    isSysAdmin: boolean;
    isExportingToExcel: boolean;
    exportToExcel: (searchParams: ISearchEndedListing) => void;
    preSetSearchParams?: ISearchEndedListing;
}

export const ListingsEndedSearchForm: React.FunctionComponent<ISearchEndedListingForm> = ({ searchEndedListings, isParaply, isSysAdmin, isExportingToExcel, exportToExcel, preSetSearchParams }) => {
    const auth = useAuth();
    const api = new ListingsApi(auth);
    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>([]);
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);
    const [selectedUmbrellaOrganisationId, setSelectedUmbrellaOrganisationId] = React.useState<number>();

    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);
    const [resetCareProviderValue, doResetCareProviderValue] = React.useState<boolean>(false);
    const [firstTimeLoaded, setFirstTimeLoaded] = React.useState<boolean>(true);
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    React.useEffect(() => {
        getUmbrellaOrganisations(preSetSearchParams?.umbrellaCareProviderId);
    }, [])

    const getUmbrellaOrganisations = (umbrellaCareProviderId?: number) => {
        setIsFetchingUmbrellaOrganisations(true);
        api.getUmbrellaOrganisations((umbrellaOrganisations) => {
            const umbrellaCareProviderIdToUse = umbrellaCareProviderId ?? umbrellaOrganisations[0].careProviderId;
            setSelectedUmbrellaOrganisationId(umbrellaCareProviderIdToUse)
            setUmbrellaOrganisations(umbrellaOrganisations);
            getUmbrellaCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations)
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getUmbrellaCareProvidersByUmbrellaCareProviderId = (umbrellaCareProviderId: number, umbrellaOrganisations: ICareProvider[]) => {
        setIsFetchingCareProviders(true);
        api.getCareProvidersWithEndedListings(umbrellaCareProviderId, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                umbrellaCareProviderId = parseInt(umbrellaCareProviderId.toString());
                let umbrellaCareProvider = umbrellaOrganisations.find(c => c.careProviderId === umbrellaCareProviderId);
                setCareProviders([umbrellaCareProvider!]);
            }

            if (firstTimeLoaded) {
                document.getElementById('submitbutton')?.click();
                setFirstTimeLoaded(false);
            }
        }, undefined, () => {
            setIsFetchingCareProviders(false);
        });
    }

    const onUmbrellaCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (isSysAdmin) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getUmbrellaCareProvidersByUmbrellaCareProviderId(parseInt(selectedCareProvider), umbrellaOrganisations);
                setSelectedUmbrellaOrganisationId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaOrganisationId(undefined);
                setCareProviders([]);
            }
            doResetCareProviderValue(prev => !prev);
        }
    }

    const getUmbrellaCareProviderDefaultValue = () => {
        if (selectedUmbrellaOrganisationId) {
            return selectedUmbrellaOrganisationId.toString();
        }

        if (umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders && careProviders.length > 0) {
            const preSetSerachParamsCareProviderId = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;
            const isPreSetCareProviderIdInCareProviders = careProviders.some((careProvider) => careProvider.careProviderId === preSetSerachParamsCareProviderId);

            if (isPreSetCareProviderIdInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const search = (data: ISearchEndedListing) => {
        if (data.careProviderId) {
            if (firstTimeLoaded && preSetSearchParams?.pageNumber) {
                data.pageNumber = preSetSearchParams.pageNumber;
                if (preSetSearchParams.sortOrder) {
                    data.sortOrder = preSetSearchParams.sortOrder;
                }
                if (preSetSearchParams.propertyToOrderBy) {
                    data.propertyToOrderBy = preSetSearchParams.propertyToOrderBy ?? undefined;
                }
            }
            searchEndedListings(data);
        }
    }

    const getDateFromDefaultValue = () => {
        if (preSetSearchParams) {
            return preSetSearchParams.dateFrom ? new Date(preSetSearchParams.dateFrom) : undefined
        }
        else {
            return startOfMonth;
        }
    }

    const getDateToDefaultValue = () => {
        if (preSetSearchParams) {
            return preSetSearchParams.dateTo ? new Date(preSetSearchParams.dateTo) : undefined
        }
        else {
            return endOfMonth;
        }
    }

    return (
        <section>
            <Form onSubmit={search}>
                <div className="row">
                    <div className="col-6">
                        <CareProviderDropdown
                            name="umbrellaCareProviderId"
                            label="Vårdgivare"
                            isLoading={isFetchingUmbrellaOrganisations}
                            careProviders={umbrellaOrganisations}
                            onValueChange={onUmbrellaCareProviderChange}
                            defaultValue={getUmbrellaCareProviderDefaultValue()}
                            disabled={!isSysAdmin}
                            useFixedListItemHeight
                            showOrgNum
                            placeholder="Välj vårdgivare"
                            isClearable
                            required
                        />
                    </div>
                    <div className="col-6" />
                    <div className="col-6">
                        <CareProviderDropdown
                            name="careProviderId"
                            label="Mottagning"
                            isLoading={isFetchingCareProviders}
                            careProviders={careProviders ?? []}
                            defaultValue={getCareProviderDefaultValue()}
                            disabled={!(isParaply || isSysAdmin)}
                            useFixedListItemHeight
                            showClinicId
                            isClearable
                            required
                            resetValue={resetCareProviderValue}
                        />
                    </div>
                    <div className="col-6" />
                    <div className="col-6">
                        <DatePickerRange label="Datum" name="dateFrom" nameSecondary="dateTo" inlineLabel
                            value={getDateFromDefaultValue()}
                            valueSecondary={getDateToDefaultValue()} />
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton id="submitbutton">Sök</CustomSubmitButton>
                            </span>
                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} isLoading={isExportingToExcel} onButtonSubmit={exportToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till Excel</CustomSubmitButton>
                            <CustomSubmitButton id="submitbutton" >Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </section>
    )
}